import { supabase } from "@/integrations/supabase/client";

const COOKIE_NAME = "ec_ref";
const COOKIE_DAYS = 90;

function readRef(): string | null {
  // Cookie first
  const m = document.cookie.match(new RegExp("(?:^|; )" + COOKIE_NAME + "=([^;]*)"));
  if (m && m[1]) return decodeURIComponent(m[1]);
  try {
    return localStorage.getItem(COOKIE_NAME);
  } catch { return null; }
}

function writeRef(value: string) {
  const d = new Date();
  d.setTime(d.getTime() + COOKIE_DAYS * 24 * 60 * 60 * 1000);
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(value)}; expires=${d.toUTCString()}; path=/; SameSite=Lax`;
  try { localStorage.setItem(COOKIE_NAME, value); } catch {}
}

function clearRef() {
  document.cookie = `${COOKIE_NAME}=; Max-Age=0; path=/`;
  try { localStorage.removeItem(COOKIE_NAME); } catch {}
}

/**
 * Persists a referral code arriving as `?ref=CODE` on ANY page (the /r/:code
 * redirect lands on `/?ref=CODE`, and affiliates also share raw `?ref=` links).
 * First-touch wins: an existing marker is never overwritten.
 */
export function captureRefFromUrl(search?: string): string | null {
  try {
    const params = new URLSearchParams(search ?? window.location.search);
    const raw = params.get("ref");
    if (!raw) return null;
    const code = raw.trim().toUpperCase().slice(0, 32);
    if (!/^[A-Z0-9-]{4,32}$/.test(code)) return null;
    if (readRef()) return readRef();
    writeRef(code);
    return code;
  } catch {
    return null;
  }
}

/**
 * Attempts to attribute a newly-signed-up courier to the referring affiliate
 * stored in the ec_ref cookie. Goes through the `affiliate-claim-attribution`
 * edge function which derives IP/fingerprint server-side (anti-spoof) and
 * calls the privileged RPC with service role.
 *
 * Safe to call on every authenticated courier page load: it no-ops without a
 * marker and clears the marker once the server has given a final answer.
 */
export async function claimReferralIfPresent(_courierId?: string) {
  const code = readRef();
  if (!code) return null;
  try {
    const { data, error } = await supabase.functions.invoke(
      "kb-ops/fn/affiliate-claim-attribution",
      { body: { code, click_meta: {} } },
    );
    if (error) return null;
    // Any definitive server answer (claimed, already attributed, unknown code,
    // self-referral, …) ends the attempt — keep the marker only on transport
    // failures so a flaky network can retry on the next load.
    clearRef();
    return data;
  } catch {
    return null;
  }
}
